(() => {
  const namespace = 'NatureliaCollectionFeatured';
  if (window[namespace]) {
    window[namespace].init();
    return;
  }
  const marker = 'naturelia_featured';
  const known = ['9247530189109', '9247505711413', '9247471534389', '9238084190517', '8119449616693', '9236499104053', '9236495761717', '9236892942645'];
  const ranks = new Map(known.map((id, rank) => [id, rank]));
  const maximumPages = 4;
  let pending;
  let revision = 0;

  const hasFilters = (url) => [...url.searchParams.keys()].some(key => key.startsWith('filter.') || ['constraint', 'q'].includes(key));
  const collectionHandles = ['palm-leaf-dinnerware', 'tableware'];
  const collectionSelector = collectionHandles.map(handle => `[data-naturelia-collection="${handle}"]`).join(',');
  const eligiblePath = (url) => collectionHandles.some(handle => url.pathname.replace(/\/$/, '').endsWith(`/collections/${handle}`));
  const isCurated = (url) => eligiblePath(url) && !hasFilters(url) && url.searchParams.get('sort_by') === 'manual' && url.searchParams.get(marker) === '1';
  const defaultURL = (url) => {
    if (!eligiblePath(url) || hasFilters(url) || url.searchParams.has('sort_by')) return null;
    const next = new URL(url.href);
    next.searchParams.set('sort_by', 'manual');
    next.searchParams.set(marker, '1');
    return next;
  };
  const order = (records) => [...records].sort((a, b) => {
    if (a.available !== b.available) return a.available ? -1 : 1;
    if (a.available) {
      const rankA = ranks.get(a.id) ?? known.length;
      const rankB = ranks.get(b.id) ?? known.length;
      if (rankA !== rankB) return rankA - rankB;
    }
    return a.position - b.position;
  });
  const pageOf = (records, page, size) => records.slice((page - 1) * size, page * size);
  const metadata = (grid) => {
    const meta = { page: Number(grid.dataset.nfcPage), pages: Number(grid.dataset.nfcPages), items: Number(grid.dataset.nfcItems), size: Number(grid.dataset.nfcSize), section: grid.dataset.nfcSection, collection: grid.dataset.nfcCollection };
    if (![meta.page, meta.pages, meta.items, meta.size].every(Number.isSafeInteger)) throw new Error('Invalid pagination metadata');
    if (meta.pages < 1 || meta.pages > maximumPages || meta.page < 1 || meta.page > meta.pages || meta.size !== 24 || meta.items < 1 || meta.items > meta.pages * meta.size || Math.ceil(meta.items / meta.size) !== meta.pages || !meta.section || !collectionHandles.includes(meta.collection)) throw new Error('Unsupported collection metadata');
    return meta;
  };
  const readPage = (grid, expected, page) => {
    const meta = metadata(grid);
    if (meta.page !== page || ['pages', 'items', 'size', 'section', 'collection'].some(key => meta[key] !== expected[key])) throw new Error('Collection changed during rendering');
    const nodes = [...grid.querySelectorAll(':scope > [data-nfc-product-id]')];
    const count = Math.min(meta.size, meta.items - (page - 1) * meta.size);
    if (nodes.length !== count) throw new Error('Incomplete collection page');
    return nodes.map((node, index) => {
      const id = node.dataset.nfcProductId;
      const available = node.dataset.nfcAvailable;
      if (!/^\d+$/.test(id) || !['true', 'false'].includes(available)) throw new Error('Invalid product data');
      return { id, available: available === 'true', position: (page - 1) * meta.size + index, node };
    });
  };
  const cancel = () => {
    revision += 1;
    pending?.abort();
    pending = undefined;
    const url = new URL(window.location.href);
    const scope = document.querySelector(collectionSelector);
    const grid = scope?.querySelector('[data-nfc-section]');
    if (grid) {
      grid.dataset.nfcState = 'native';
      grid.setAttribute('aria-busy', 'false');
    }
    scope?.querySelectorAll('.pagination a[href]').forEach(link => {
      const href = link.getAttribute('href');
      if (!href || href === '#') return;
      const next = new URL(href, url.href);
      if (next.origin !== url.origin || next.pathname !== url.pathname) return;
      next.searchParams.delete(marker);
      link.setAttribute('href', next.pathname + next.search + next.hash);
    });
    if (url.searchParams.has(marker)) {
      url.searchParams.delete(marker);
      window.history.replaceState(window.history.state, '', url.href);
    }
  };
  const preservePagination = (scope, current) => {
    scope.querySelectorAll('.pagination a[href]').forEach(link => {
      const href = link.getAttribute('href');
      if (!href || href === '#') return;
      const next = new URL(href, current.href);
      if (next.origin !== current.origin || next.pathname !== current.pathname) return;
      for (const [key, value] of current.searchParams) {
        if (!next.searchParams.has(key)) next.searchParams.append(key, value);
      }
      next.searchParams.set('sort_by', 'manual');
      next.searchParams.set(marker, '1');
      link.setAttribute('href', next.pathname + next.search + next.hash);
    });
  };
  const curate = async (scope, url) => {
    const grid = scope.querySelector('[data-nfc-section]');
    if (!grid || grid.dataset.nfcOrderedURL === url.href || grid.dataset.nfcOrderingURL === url.href) return;
    const version = ++revision;
    pending?.abort();
    const controller = new AbortController();
    pending = controller;
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    grid.dataset.nfcOrderingURL = url.href;
    grid.dataset.nfcState = 'loading';
    grid.setAttribute('aria-busy', 'true');
    try {
      const meta = metadata(grid);
      const local = readPage(grid, meta, meta.page);
      const requests = [];
      for (let page = 1; page <= meta.pages; page += 1) {
        if (page === meta.page) continue;
        const request = new URL(url.href);
        request.hash = '';
        request.searchParams.set('page', String(page));
        request.searchParams.set('section_id', meta.section);
        request.searchParams.delete('sections');
        requests.push(fetch(request.href, { credentials: 'same-origin', signal: controller.signal }).then(async response => {
          if (!response.ok) throw new Error('Section rendering request failed');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextGrid = html.querySelector('[data-nfc-section]');
          if (!nextGrid) throw new Error('Collection section missing');
          return readPage(nextGrid, meta, page);
        }));
      }
      const records = [...local, ...(await Promise.all(requests)).flat()];
      if (records.length !== meta.items || new Set(records.map(record => record.id)).size !== meta.items) throw new Error('Collection incomplete or duplicated');
      if (version !== revision || !grid.isConnected || window.location.href !== url.href || !isCurated(new URL(window.location.href))) return;
      const ordered = order(records);
      const page = pageOf(ordered, meta.page, meta.size);
      grid.replaceChildren(...page.map(record => record.node));
      grid.dataset.nfcOrderedURL = url.href;
      grid.dataset.nfcState = 'complete';
      preservePagination(scope, url);
      document.dispatchEvent(new CustomEvent('paginate'));
    } catch {
      if (version === revision) grid.dataset.nfcState = 'fallback';
    } finally {
      window.clearTimeout(timeout);
      grid.setAttribute('aria-busy', 'false');
      delete grid.dataset.nfcOrderingURL;
      if (pending === controller) pending = undefined;
    }
  };
  const init = () => {
    const scope = document.querySelector(collectionSelector);
    if (!scope) return;
    const url = new URL(window.location.href);
    const next = defaultURL(url);
    if (next) {
      window.location.replace(next.href);
      return;
    }
    if (isCurated(url)) {
      curate(scope, url);
    } else if (url.searchParams.has(marker)) {
      cancel();
    }
  };
  const onShopperControl = (event) => {
    const scope = event.target.closest?.(collectionSelector);
    if (!scope) return;
    if (event.target.closest('#CollectionFiltersForm, .js-facet-remove')) cancel();
  };
  window[namespace] = { init, isCurated, defaultURL, order, pageOf, metadata, readPage, curate, cancel };
  ['input', 'change', 'submit'].forEach(name => document.addEventListener(name, onShopperControl, true));
  document.addEventListener('click', event => {
    if (event.target.closest?.('.js-facet-remove')) onShopperControl(event);
  }, true);
  document.addEventListener('paginate', init);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
